import { PAYMENT_STATUS_DELIVERED } from 'constants/payment';
import type { PaymentWithEntities } from 'models/payment';
import { ChargeIntent } from 'models/recurringPayment';
import type { RootState } from 'reducers/types';
import { getRecipientId, isRecipientCAP } from '../recipient/recipient';
import { getRecipientById } from '../recipients/recipients';
import { getUIPaymentStatus } from '../utils/payment/payment';

export const getPayments = (state: RootState) => {
  const {
    entities: { payments },
  } = state;

  return payments.map(payment => {
    const { id, purchase, recipient, createdAt, offer } = payment;
    const UIStatus = getUIPaymentStatus(recipient, payment);
    let recipientWithAllInfo = getRecipientById(state, recipient.id);

    if (isRecipientCAP(recipient)) {
      const { name, metadata } = recipientWithAllInfo || {};
      recipientWithAllInfo = { ...recipient, name, metadata };
    }

    return {
      id: id,
      status: UIStatus,
      purchase: purchase,
      recipient: recipientWithAllInfo,
      createdAt: createdAt,
      offer: offer,
      hasRefunds: !!getPaymentLink(payment, 'refunds'),
      chargeIntent: payment.chargeIntent,
    };
  });
};

export const getRecipientsIds = (state: RootState) => {
  const {
    entities: { payments },
  } = state;
  const ids = payments.map(payment => payment.recipient.id);
  const idsWithoutDuplicates = Array.from(new Set(ids));

  return idsWithoutDuplicates;
};

export const getPaymentById = (state: RootState, id: string) => {
  const {
    entities: { payments },
  } = state;

  return payments.find(payment => payment.id === id);
};

export const getLastMatchingPaymentFromRecipient = (state: RootState) => {
  const recipientId = getRecipientId(state);

  const {
    entities: { payments },
  } = state;

  return payments.find(payment => payment.recipient.id === recipientId);
};

const getPaymentLink = (payment: PaymentWithEntities, linkRel: string) => {
  return payment.links && payment.links.find(link => link.rel === linkRel);
};

const hasRecurringId = (payment: { chargeIntent?: ChargeIntent }) => {
  const recurringId = payment.chargeIntent?.recurringId;
  return !!recurringId && String(recurringId).trim() !== '';
};

export const canDuplicatePayment = (state: RootState, paymentId: string) => {
  const payment = getPayments(state).find(({ id }) => id === paymentId);

  if (!payment) {
    return false;
  }

  const {
    recipient: {
      metadata: {
        gatewayOnly = { enabled: false },
        clientPrepopulated = false,
      } = {},
    } = {},
    status,
  } = payment;

  if (gatewayOnly.enabled || clientPrepopulated) {
    return false;
  }

  if (hasRecurringId(payment)) {
    return false;
  }

  return status === PAYMENT_STATUS_DELIVERED;
};
